import { defineStore } from "pinia";

import { API, HEADLAMP_LOGOUT } from "@/axios-config";
import { clearStoredSession, isTokenDead, isWellFormedToken, tokenExpiryMs } from "@/utils/authSession";
import { DEFAULT_SITE_ID, hubId, siteAndVersionFromHubId } from "@/config/jupyterhubs";

// Version is parsed from "jh_version" regardless of which shape it holds -
// plain "3"/"4" (the original ARCUS-only toggle) or, briefly for part of
// 2026-09-11, a full combined hub id like "imperial-v3" (a short-lived
// single-selector design that predates the site/version split below).
const _migratedVersion = siteAndVersionFromHubId(localStorage.getItem("jh_version")).version;

// A token left in localStorage by an earlier bug ("undefined", "null", a cut-off string) used to make jwt_decode throw inside the
// router guard, which left the page blank and impossible to leave until the site data was cleared. Anything that is not a
// readable JWT is treated as no token at all and removed.
function _storedToken(key) {
    const v = localStorage.getItem(key);
    if (v === null) return null;
    if (!isWellFormedToken(v)) {
        localStorage.removeItem(key);
        return null;
    }
    return v;
}

export const useUserStore = defineStore({
    id: "user",
    state: () => ({
        token: _storedToken("token"),
        refreshToken: _storedToken("refreshToken"),
        user: localStorage.getItem("user") || null,
        uuid: localStorage.getItem("uuid") || null,
        otp_verified: localStorage.getItem("otp_verified") || null,
        otp_validated: localStorage.getItem("otp_validated") || null,
        profile: localStorage.getItem("profile") || null,
        jupyterToken: localStorage.getItem("jupyterToken") || null,
        isSuperuser: localStorage.getItem("isSuperuser") || false,
        // JupyterHub *version* (Profile setting) and *site* (chosen on the
        // spawn page itself, JupyterLab.vue) are now separate pieces of
        // state - see src/config/jupyterhubs.js. `hub` below is computed
        // from both, so every existing call site that reads
        // `userStore.hub` (the id actually sent to the backend) keeps
        // working unchanged.
        hubVersion: _migratedVersion,
        // Deliberately NOT persisted to localStorage (unlike hubVersion) -
        // site is a per-spawn decision made on the spawn page itself, and
        // should always start back at ARCUS rather than silently
        // remembering whatever site was last used, possibly days ago.
        hubSite: DEFAULT_SITE_ID,
    }),
    getters: {
        getToken: (state) => state.token,
        getRefreshToken: (state) => state.refreshToken,
        getUser: (state) => JSON.parse(state.user),
        getUID: (state) => JSON.parse(state.uid),
        getProfile: (state) => JSON.parse(state.profile),
        getTokenExpirationDate(state) {
            // null for no token, for junk and for a token without exp (all of which isTokenExpired() reports as expired)
            return state.token ? tokenExpiryMs(state.token) : null;
        },
        getJupyterToken: (state) => JSON.parse(state.jupyterToken),
        getJupyterHubVersion: (state) => state.hubVersion,
        getJupyterHubSite: (state) => state.hubSite,
        // The actual id sent to the backend (servers/token/images/etc) -
        // combines the two pieces of state above. Unchanged shape from
        // before the site/version split, so every existing
        // `userStore.hub` read keeps working.
        hub: (state) => hubId(state.hubSite, state.hubVersion),
    },
    actions: {
        isTokenExpired() {
            const expirationDate = this.getTokenExpirationDate;
            if (!expirationDate) { return true; }
            // let exp = Date.parse(expirationDate);
            const now = new Date();
            return expirationDate < now.getTime()
            //let diff = now.getTimezoneOffset();
            // return expirationDate < (now.getTime() + diff * 60 * 1000)
        },
        isLoggedIn() {
            if (this.otp_verified) {
                if (this.otp_validated) {
                    return !this.isTokenExpired();
                }
            } else {
                return !this.isTokenExpired();
            }
            return false;
        },
        setJupyterHubVersion(jh_version) {
            this.hubVersion = jh_version;
            localStorage.setItem("jh_version", this.hubVersion);
        },
        setJupyterHubSite(jh_site) {
            // In-memory only, deliberately not persisted - see the hubSite
            // state comment above.
            this.hubSite = jh_site;
        },
        // True when the refresh token is missing, junk or past its own expiry (7 days): refreshing is pointless then.
        isRefreshTokenDead() {
            return isTokenDead(this.refreshToken);
        },
        setToken(token) {
            if (!isWellFormedToken(token)) {
                // never store "undefined": see _storedToken
                this.removeToken();
                return;
            }
            this.token = token;

            // Save token to local storage
            localStorage.setItem("token", this.token);
        },
        setJupyterToken(token) {
            this.jupyterToken = JSON.stringify(token);

            // Save token to local storage
            localStorage.setItem("jupyterToken", this.jupyterToken);
        },
        setRefreshToken(refreshToken) {
            if (!isWellFormedToken(refreshToken)) {
                this.refreshToken = null;
                localStorage.removeItem("refreshToken");
                return;
            }
            this.refreshToken = refreshToken;

            // Save token to local storage
            localStorage.setItem("refreshToken", this.refreshToken);
        },
        removeToken() {
            this.token = null;
            this.refreshToken = null;
            this.jupyterToken = null;

            // Delete token from local storage
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("jupyterToken");
        },
        setUser(user) {
            this.user = JSON.stringify(user);
            this.isSuperuser = user.is_staff;
            this.uuid = user.uuid;
            this.otp_verified = user.otp_verified;

            // Save user to local storage
            localStorage.setItem("user", this.user);
            localStorage.setItem("uuid", this.uuid);
            localStorage.setItem("otp_verified", this.otp_verified);
            localStorage.setItem("isSuperuser", this.isSuperuser);
        },
        setProfile(profile) {
            this.profile = JSON.stringify(profile);

            // Save user to local storage
            localStorage.setItem("profile", this.profile);
        },
        setOTP(value) {
            this.otp_validated = value;
            localStorage.setItem("otp_validated", this.otp_validated);
        },
        removeUser() {
            this.user = null;
            this.profile = null;
            this.uuid = null;
            this.otp_verified = null;
            this.otp_validated = null;
            this.isSuperuser = false;

            // Delete user from local storage
            localStorage.removeItem("user");
            localStorage.removeItem("uuid");
            localStorage.removeItem("otp_verified");
            localStorage.removeItem("otp_validated");
            localStorage.removeItem("profile");
            localStorage.removeItem("isAdmin");
            localStorage.removeItem("isSuperuser");
        },
        // Another tab signed in or out: take over what it left in localStorage (the storage event fires in the OTHER tabs). Without
        // this a tab that was open all along kept the old token in memory, refreshed it on its next navigation and wrote it back,
        // overwriting the sign-in of the tab the person actually used (and of a different account, if they switched).
        syncFromStorage() {
            this.token = _storedToken("token");
            this.refreshToken = _storedToken("refreshToken");
            this.user = localStorage.getItem("user") || null;
            this.uuid = localStorage.getItem("uuid") || null;
            this.otp_verified = localStorage.getItem("otp_verified") || null;
            this.otp_validated = localStorage.getItem("otp_validated") || null;
            this.profile = localStorage.getItem("profile") || null;
            this.jupyterToken = localStorage.getItem("jupyterToken") || null;
            this.isSuperuser = localStorage.getItem("isSuperuser") || false;
        },
        // Forget the stored sign-in WITHOUT contacting the server: for a token that is already dead, and at the start of every new
        // sign-in attempt (password or CRSid) so nothing left over from an earlier attempt can leak into it.
        clearSession() {
            this.token = null;
            this.refreshToken = null;
            this.jupyterToken = null;
            this.removeUser();
            clearStoredSession(localStorage);
        },
        logout() {
            // Best-effort: revokes the Headlamp session cookie, if one exists.
            // Without this, "Log out" would only clear the SPA's own JWT and
            // leave a live cluster-admin cookie behind for hours. Fire-and-forget
            // since this store's own token/user cleanup below must not block on it.
            API.post(HEADLAMP_LOGOUT).catch(() => {});
            this.removeToken();
            this.removeUser();
        }
    },
});
